/**
 * The filter-operator vocabulary — the single home for which operators a field
 * may offer, how one is resolved from URL state, and how it reads in a chip.
 * PURE: no React, no I/O. The wire encoding of each operator lives in
 * `codecs.ts`; this module only names and resolves them.
 */

import type { FilterField, FilterFieldType, FilterOp } from "./types";

/**
 * Operators offered per field type when a descriptor declares none. Each entry
 * reproduces the wire that type emitted before operators were selectable, so an
 * un-migrated field keeps its exact previous behavior and renders its operator
 * as static text (a one-operator chip has nothing to choose).
 */
export const DEFAULT_OPERATORS: Record<FilterFieldType, readonly FilterOp[]> = {
  multiSelect: ["in"],
  multiEntity: ["in"],
  singleSelect: ["eq"],
  entity: ["eq"],
  boolean: ["eq"],
  dateRange: ["range"],
  priceRange: ["range"],
};

/** Operators whose value is a single lower bound (the range widgets' `start`/`min`). */
export const LOWER_BOUND_OPERATORS: readonly FilterOp[] = ["gt", "gte"];

/** Operators whose value is a single upper bound (the range widgets' `end`/`max`). */
export const UPPER_BOUND_OPERATORS: readonly FilterOp[] = ["lt", "lte"];

/**
 * The two types whose operator is DERIVED from the value instead of stored in
 * the URL: their value object already says which bounds are filled, and
 * `start`/`min` alone IS "is on or after", `end`/`max` alone IS "is on or
 * before". Storing that a second time would let the two disagree.
 *
 * Two consequences, both enforced by `defineFilterFields`: the STRICT
 * comparisons (`gt`/`lt`) have no encoding here — a filled lower bound can only
 * mean `gte` — and these types cannot offer an operator DROPDOWN, since picking
 * one would have to rewrite the value. They may declare only their two-bound
 * encoding (`range` or `between`).
 */
export const DERIVED_OPERATOR_TYPES: readonly FilterFieldType[] = [
  "dateRange",
  "priceRange",
];

export function hasDerivedOperator(type: FilterFieldType): boolean {
  return DERIVED_OPERATOR_TYPES.includes(type);
}

/** Which bounds of a range value are filled, across both key pairs. */
function filledBounds(value: unknown): { low: boolean; high: boolean } {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { low: false, high: false };
  }
  const v = value as Record<string, unknown>;
  const filled = (bound: unknown) => bound != null && bound !== "";
  return {
    low: filled(v.start) || filled(v.min),
    high: filled(v.end) || filled(v.max),
  };
}

/**
 * The two-bound encoding a range field sends when BOTH bounds are filled.
 * `range` (`gte` + `lte`) unless the field declares the platform's compact
 * `between` pair; the comparison operators in a declared list name menu
 * entries, never the encoding.
 */
function rangeEncoding(
  field: Pick<FilterField, "type" | "operators">,
): FilterOp {
  return (
    operatorsForField(field).find((op) => op === "range" || op === "between") ??
    "range"
  );
}

/**
 * A range field's active operator, read off its value: one bound filled means
 * the matching inclusive comparison, both (or a bare preset label such as
 * "No completed date", which carries neither) means the field's encoding.
 */
export function deriveRangeOperator(
  field: Pick<FilterField, "type" | "operators">,
  value: unknown,
): FilterOp {
  const { low, high } = filledBounds(value);
  if (low && !high) return "gte";
  if (high && !low) return "lte";
  return rangeEncoding(field);
}

const BASE_LABELS: Record<FilterOp, string> = {
  eq: "is",
  neq: "is not",
  in: "is any of",
  notIn: "is none of",
  range: "is between",
  between: "is between",
  gt: "is greater than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
};

/** Date fields read as points in time, not magnitudes. */
const DATE_LABELS: Partial<Record<FilterOp, string>> = {
  gt: "is after",
  gte: "is on or after",
  lt: "is before",
  lte: "is on or before",
};

/**
 * The operators a field offers, in menu order. `operators[0]` is the field's
 * default — the one used until the user picks another.
 */
export function operatorsForField(
  field: Pick<FilterField, "type" | "operators">,
): readonly FilterOp[] {
  const declared = field.operators;
  return declared && declared.length > 0
    ? declared
    : DEFAULT_OPERATORS[field.type];
}

/**
 * The active operator for a field. Two sources, dispatched by type:
 *
 * - `dateRange` / `priceRange` derive it from `value` — the URL stores no
 *   operator for them (see `DERIVED_OPERATOR_TYPES`).
 * - every other type reads `chosen`, the operator parsed out of the URL's
 *   `filters[key][op]` bracket. An unknown or no-longer-offered operator falls
 *   back to the field's default instead of throwing, so a stale or hand-edited
 *   link still renders a working chip.
 */
export function resolveOperator(
  field: Pick<FilterField, "type" | "operators">,
  chosen: string | undefined,
  value?: unknown,
): FilterOp {
  if (hasDerivedOperator(field.type)) return deriveRangeOperator(field, value);
  const allowed = operatorsForField(field);
  return chosen && (allowed as readonly string[]).includes(chosen)
    ? (chosen as FilterOp)
    : allowed[0];
}

/** What the CURRENT value looks like — the parts of the wording it decides. */
export interface OperatorLabelContext {
  /**
   * How many values are selected. One pick reads as the singular form ("Status
   * is Active" rather than "Status is any of Active"), matching the chip
   * wording that predates operators.
   */
  selectedCount?: number;
  /**
   * A date value that is an explicit two-date range (from the custom picker)
   * rather than a named preset. A preset label is already a noun phrase that
   * reads naturally after "is" — "Due date is Today", not "is between Today" —
   * whereas two dates need "is between" to make sense.
   */
  isExplicitRange?: boolean;
}

/**
 * Chip wording for an operator, given what the current value looks like. Pass no
 * context to name the operator itself (what the dropdown menu wants).
 */
export function operatorLabel(
  operator: FilterOp,
  type: FilterFieldType,
  context: OperatorLabelContext = {},
): string {
  const { selectedCount = 0, isExplicitRange = false } = context;

  if (type === "dateRange") {
    const dateLabel = DATE_LABELS[operator];
    if (dateLabel) return dateLabel;
    if (operator === "range" || operator === "between") {
      return isExplicitRange ? BASE_LABELS.between : BASE_LABELS.eq;
    }
  }
  if (selectedCount === 1) {
    if (operator === "in") return BASE_LABELS.eq;
    if (operator === "notIn") return BASE_LABELS.neq;
  }
  return BASE_LABELS[operator];
}

/**
 * The chip dropdown's option list for a field. Menu entries name the OPERATOR,
 * not the current value, so a date range always reads its full "is between"
 * form here — otherwise it would sit in the menu as a bare "is" next to
 * "is after" / "is before" with nothing to distinguish it.
 */
export function operatorOptions(
  field: Pick<FilterField, "type" | "operators">,
): { value: FilterOp; label: string }[] {
  return operatorsForField(field).map((operator) => ({
    value: operator,
    label: operatorLabel(operator, field.type, { isExplicitRange: true }),
  }));
}
